import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  ShoppingCart,
  Store,
  Package,
  Heart,
  Search,
  Percent,
  Wallet,
  IndianRupee,
  Briefcase,
  BarChart3,
  ListFilter,
  Download,
  CalendarRange,
  RotateCcw,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type {
  CommissionRow,
  ProductSaleRow,
  StockRow,
  WalletRechargeRow,
  WishlistRow,
  SearchRow,
  DateRangeParams,
} from '@terra/api-client';
import { Badge, type BadgeTone, EmptyState, Spinner } from '@terra/ui';
import { formatPriceShort } from '@terra/utils';

import { api } from '../lib/api';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
/** Indian dashboard figure: ₹2,435 · ₹1.25 L · ₹2.5 Cr (never "K"). */
const compactInr = (p: number) => formatPriceShort(p || 0);

function NothingFound() {
  return (
    <div className="py-16">
      <EmptyState title="No data" description="Nothing found for this report yet." />
    </div>
  );
}

// ── DateRangeBar ─────────────────────────────────────────────────────────────

interface DateRangeBarProps {
  range: DateRangeParams;
  onApply: (range: DateRangeParams) => void;
  onExport: () => void;
  exportLabel?: string;
}

function DateRangeBar({ range, onApply, onExport, exportLabel = 'Export CSV' }: DateRangeBarProps) {
  const [draft, setDraft] = useState<DateRangeParams>(range);

  function handleApply() {
    onApply(draft);
  }

  function handleReset() {
    setDraft({});
    onApply({});
  }

  const hasFilter = !!(range.from || range.to);

  return (
    <div className="border-border/60 bg-surface-subtle/30 flex flex-col flex-wrap items-start gap-3 border-b px-5 py-4 sm:flex-row sm:items-center">
      {/* Icon + label */}
      <div className="flex shrink-0 items-center gap-2">
        <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
          <CalendarRange className="h-4 w-4" strokeWidth={2} />
        </div>
        <span className="text-ink-muted text-[12px] font-bold uppercase tracking-wider">
          Date Range
        </span>
      </div>

      {/* Date inputs */}
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <input
          id="report-from"
          type="date"
          value={draft.from ?? ''}
          onChange={(e) =>
            setDraft(
              (d: DateRangeParams) =>
                ({ ...d, from: e.target.value || undefined }) as DateRangeParams,
            )
          }
          className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-9 rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2"
        />
        <span className="text-ink-muted text-[12px] font-bold">to</span>
        <input
          id="report-to"
          type="date"
          value={draft.to ?? ''}
          onChange={(e) =>
            setDraft(
              (d: DateRangeParams) =>
                ({ ...d, to: e.target.value || undefined }) as DateRangeParams,
            )
          }
          className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-9 rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2"
        />
        <button
          id="report-apply-filter"
          onClick={handleApply}
          className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          Apply
        </button>
        {hasFilter && (
          <button
            id="report-reset-filter"
            onClick={handleReset}
            title="Reset to all-time"
            className="border-border/60 bg-surface text-ink-muted hover:text-ink flex h-9 items-center gap-1.5 rounded-xl border px-3 text-[13px] font-medium transition-colors"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
        )}
      </div>

      {/* Export */}
      <button
        id="report-export-csv"
        onClick={onExport}
        className="border-border/60 bg-surface hover:bg-surface-subtle text-ink flex h-9 shrink-0 items-center gap-1.5 rounded-xl border px-4 text-[13px] font-bold shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
      >
        <Download className="h-4 w-4" />
        {exportLabel}
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// EARNING REPORT
// ══════════════════════════════════════════════════════════════════

function BarList({ rows }: { rows: Array<{ label: string; amountPaise: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.amountPaise));
  return (
    <div className="space-y-4">
      {rows.map((r) => (
        <div key={r.label} className="group">
          <div className="mb-1.5 flex items-center justify-between text-[13px]">
            <span className="text-ink-muted group-hover:text-ink font-bold transition-colors">
              {r.label}
            </span>
            <span className="text-ink font-extrabold">{formatPaise(r.amountPaise)}</span>
          </div>
          <div className="bg-surface-subtle border-border/40 h-2.5 overflow-hidden rounded-full border">
            <div
              className="bg-gold-500 h-full rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${(r.amountPaise / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EarningReportPage() {
  const [range, setRange] = useState<DateRangeParams>({});
  const report = useQuery({
    queryKey: ['report-earnings', range],
    queryFn: () => api.reports.earnings(range),
  });
  const d = report.data;
  const hasRange = !!(range.from || range.to);

  return (
    <div className="space-y-6 pb-8">
      {/* DateRangeBar for earnings */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <DateRangeBar
          range={range}
          onApply={setRange}
          onExport={() => window.open(api.reports.exportEarningsUrl(range), '_blank')}
        />
        {report.isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : !d ? (
          <NothingFound />
        ) : (
          <div className="space-y-6 p-6">
            {hasRange && (
              <p className="text-ink-muted text-[12px] font-medium">
                Showing data from <span className="text-ink font-bold">{range.from ?? '—'}</span> to{' '}
                <span className="text-ink font-bold">{range.to ?? 'today'}</span>
              </p>
            )}
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
              {/* Sales Card */}
              <div className="border-border/60 bg-surface hover:border-ink group relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-ink-muted mb-1 text-[12px] font-bold uppercase tracking-wider">
                      {hasRange ? 'Sales In Range' : 'Total Sales Alltime'}
                    </p>
                    <p className="text-ink text-3xl font-extrabold tracking-tight">
                      {compactInr(d.totalSalesAllTimePaise)}
                    </p>
                  </div>
                  <div className="bg-surface-subtle text-gold-500 border-border/40 grid h-12 w-12 place-items-center rounded-xl border transition-transform group-hover:scale-105">
                    <TrendingUp className="h-6 w-6 stroke-[2]" />
                  </div>
                </div>
                <div className="bg-ink mt-6 flex items-center justify-between rounded-xl px-4 py-3 text-[12px] font-bold text-white shadow-sm">
                  <span>{hasRange ? 'Period payouts' : 'Sales this month'}</span>
                  <span className="text-gold-400">{formatPaise(d.salesThisMonthPaise)}</span>
                </div>
              </div>

              {/* Payouts Card */}
              <div className="border-border/60 bg-surface hover:border-ink group relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-ink-muted mb-1 text-[12px] font-bold uppercase tracking-wider">
                      {hasRange ? 'Payouts In Range' : 'Total Payouts'}
                    </p>
                    <p className="text-ink text-3xl font-extrabold tracking-tight">
                      {compactInr(d.payoutsAllTimePaise)}
                    </p>
                  </div>
                  <div className="bg-surface-subtle border-border/40 grid h-12 w-12 place-items-center rounded-xl border text-red-500 transition-transform group-hover:scale-105">
                    <Wallet className="h-6 w-6 stroke-[2]" />
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-between rounded-xl bg-red-500 px-4 py-3 text-[12px] font-bold text-white shadow-sm">
                  <span>{hasRange ? 'Period payouts' : 'Payouts this month'}</span>
                  <span>{formatPaise(d.payoutsThisMonthPaise)}</span>
                </div>
              </div>

              {/* Category Card */}
              <div className="border-border/60 bg-surface hover:border-ink group relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-ink-muted mb-1 text-[12px] font-bold uppercase tracking-wider">
                      Total Categories
                    </p>
                    <p className="text-ink text-3xl font-extrabold tracking-tight">
                      {d.totalCategory}
                    </p>
                  </div>
                  <div className="bg-surface-subtle border-border/40 grid h-12 w-12 place-items-center rounded-xl border text-blue-500 transition-transform group-hover:scale-105">
                    <BarChart3 className="h-6 w-6 stroke-[2]" />
                  </div>
                </div>
              </div>

              {/* Brands Card */}
              <div className="border-border/60 bg-surface hover:border-ink group relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-ink-muted mb-1 text-[12px] font-bold uppercase tracking-wider">
                      Total Brands
                    </p>
                    <p className="text-ink text-3xl font-extrabold tracking-tight">
                      {d.totalBrands}
                    </p>
                  </div>
                  <div className="bg-surface-subtle border-border/40 grid h-12 w-12 place-items-center rounded-xl border text-purple-500 transition-transform group-hover:scale-105">
                    <Briefcase className="h-6 w-6 stroke-[2]" />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm">
                <div className="border-border/40 mb-6 flex items-center justify-between border-b pb-4">
                  <div>
                    <h3 className="text-ink flex items-center gap-2 text-[15px] font-extrabold">
                      <ShoppingCart className="text-gold-500 h-4 w-4" /> Net Sales Breakdown
                    </h3>
                    <p className="text-ink-muted mt-0.5 text-[12px] font-medium">
                      By Sale Category
                    </p>
                  </div>
                </div>
                <BarList rows={d.netSales} />
              </div>

              <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm">
                <div className="border-border/40 mb-6 flex items-center justify-between border-b pb-4">
                  <div>
                    <h3 className="text-ink flex items-center gap-2 text-[15px] font-extrabold">
                      <IndianRupee className="h-4 w-4 text-red-500" /> Payouts Breakdown
                    </h3>
                    <p className="text-ink-muted mt-0.5 text-[12px] font-medium">
                      By Expense Category
                    </p>
                  </div>
                </div>
                <BarList rows={d.payoutsBreakdown} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// PRODUCT SALE REPORT (in-house / seller)
// ══════════════════════════════════════════════════════════════════

function ProductSaleReport({ kind }: { title: string; kind: 'inhouse' | 'seller' }) {
  const categories = useQuery({
    queryKey: ['report-categories'],
    queryFn: () => api.reports.categories(),
  });
  const [categoryDraft, setCategoryDraft] = useState('');
  const [category, setCategory] = useState('');
  const [range, setRange] = useState<DateRangeParams>({});

  const sales = useQuery({
    queryKey: ['report-sales', kind, category, range],
    queryFn: () =>
      kind === 'inhouse'
        ? api.reports.inHouseSales(category || undefined, range)
        : api.reports.sellerSales(category || undefined, range),
  });
  const rows = sales.data ?? [];

  function handleExport() {
    const url =
      kind === 'inhouse'
        ? api.reports.exportInHouseSalesUrl(category || undefined, range)
        : api.reports.exportSellerSalesUrl(category || undefined, range);
    window.open(url, '_blank');
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {/* Filter toolbar */}
        <div className="border-border/60 bg-surface-subtle/30 flex flex-col flex-wrap items-start gap-3 border-b px-5 py-4 sm:flex-row sm:items-center">
          <div className="flex shrink-0 items-center gap-2">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <ListFilter className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink-muted text-[12px] font-bold uppercase tracking-wider">
              Filters
            </span>
          </div>

          <select
            className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-9 flex-1 rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2 sm:max-w-xs"
            value={categoryDraft}
            onChange={(e) => setCategoryDraft(e.target.value)}
          >
            <option value="">All Categories</option>
            {(categories.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={range.from ?? ''}
            onChange={(e) =>
              setRange(
                (r: DateRangeParams) =>
                  ({ ...r, from: e.target.value || undefined }) as DateRangeParams,
              )
            }
            className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-9 rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2"
          />
          <span className="text-ink-muted text-[12px] font-bold">to</span>
          <input
            type="date"
            value={range.to ?? ''}
            onChange={(e) =>
              setRange(
                (r: DateRangeParams) =>
                  ({ ...r, to: e.target.value || undefined }) as DateRangeParams,
              )
            }
            className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-9 rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2"
          />

          <button
            onClick={() => setCategory(categoryDraft)}
            className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            Apply
          </button>
          {(category || range.from || range.to) && (
            <button
              onClick={() => {
                setCategoryDraft('');
                setCategory('');
                setRange({});
              }}
              className="border-border/60 bg-surface text-ink-muted hover:text-ink flex h-9 items-center gap-1.5 rounded-xl border px-3 text-[13px] font-medium transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset
            </button>
          )}
          <button
            onClick={handleExport}
            className="border-border/60 bg-surface hover:bg-surface-subtle text-ink ml-auto flex h-9 items-center gap-1.5 rounded-xl border px-4 text-[13px] font-bold shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        <div className="p-0">
          {sales.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <NothingFound />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Product Name</th>
                    <th className="w-32 py-3 pr-5 text-right">Num of Sales</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((r: ProductSaleRow, idx: number) => (
                    <tr
                      key={r.productName}
                      className="hover:bg-surface-subtle/30 transition-colors"
                    >
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="text-ink py-3 pr-4 font-semibold">
                        <Link
                          to={`/products/all?search=${encodeURIComponent(r.productName)}`}
                          className="hover:text-brand-primary transition-colors hover:underline"
                        >
                          {r.productName}
                        </Link>
                      </td>
                      <td className="py-3 pr-5 text-right">
                        <span className="bg-surface-subtle text-ink border-border/40 inline-flex min-w-[32px] items-center justify-center rounded-lg border px-2 py-1 text-[12px] font-extrabold shadow-sm">
                          {r.numSold}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const InHouseSaleReportPage = () => (
  <ProductSaleReport title="In-house Product Sales" kind="inhouse" />
);
export const SellerSaleReportPage = () => (
  <ProductSaleReport title="Seller Product Sales" kind="seller" />
);

// ══════════════════════════════════════════════════════════════════
// PRODUCTS STOCK
// ══════════════════════════════════════════════════════════════════

export function ProductsStockReportPage() {
  const stock = useQuery({ queryKey: ['report-stock'], queryFn: () => api.reports.stock() });
  const rows = stock.data ?? [];
  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
          <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
            <Package className="h-4 w-4" strokeWidth={2} />
          </div>
          <span className="text-ink text-[14px] font-bold">Inventory Status</span>
          <button
            onClick={() => window.open(api.reports.exportStockUrl(), '_blank')}
            className="border-border/60 bg-surface hover:bg-surface-subtle text-ink ml-auto flex h-8 items-center gap-1.5 rounded-xl border px-3 text-[13px] font-bold shadow-sm transition-all hover:-translate-y-0.5"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>

        <div className="p-0">
          {stock.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <NothingFound />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Product</th>
                    <th className="py-3 pr-4">SKU</th>
                    <th className="py-3 pr-4 text-center">On Hand</th>
                    <th className="py-3 pr-4 text-center">Reserved</th>
                    <th className="py-3 pr-5 text-center">Available</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((r: StockRow, idx: number) => (
                    <tr key={r.sku} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="text-ink py-3 pr-4 font-semibold">
                        <Link
                          to={`/products/all?search=${encodeURIComponent(r.productTitle)}`}
                          className="hover:text-brand-primary transition-colors hover:underline"
                        >
                          {r.productTitle}
                        </Link>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="bg-surface-subtle text-ink-muted border-border/40 inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-mono text-[11px] font-bold">
                          {r.sku}
                        </span>
                      </td>
                      <td className="text-ink-muted py-3 pr-4 text-center font-bold">{r.onHand}</td>
                      <td className="py-3 pr-4 text-center font-bold text-red-500">{r.reserved}</td>
                      <td className="py-3 pr-5 text-center">
                        <span className="inline-flex min-w-[32px] items-center justify-center rounded-lg border border-green-200 bg-green-50 px-2 py-0.5 text-[12px] font-extrabold text-green-700 shadow-sm">
                          {r.available}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// PRODUCTS WISHLIST / USER SEARCHES
// ══════════════════════════════════════════════════════════════════

export function ProductsWishlistReportPage() {
  const [range, setRange] = useState<DateRangeParams>({});
  const wishlist = useQuery({
    queryKey: ['report-wishlist', range],
    queryFn: () => api.reports.wishlist(range),
  });
  const rows = wishlist.data ?? [];
  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-pink-50 text-pink-500 ring-1 ring-inset ring-pink-100">
            <Heart className="h-4 w-4" strokeWidth={2} />
          </div>
          <span className="text-ink text-[14px] font-bold">Customer Favorites</span>
        </div>
        <DateRangeBar
          range={range}
          onApply={setRange}
          onExport={() => window.open(api.reports.exportWishlistUrl(range), '_blank')}
        />

        <div className="p-0">
          {wishlist.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <NothingFound />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Product Name</th>
                    <th className="py-3 pr-4">SKU</th>
                    <th className="py-3 pr-4">Customer</th>
                    <th className="py-3 pr-5 text-right">Added On</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((r: WishlistRow, idx: number) => (
                    <tr key={r.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="text-ink py-3 pr-4 font-semibold">
                        <Link
                          to={`/products/all?search=${encodeURIComponent(r.productName)}`}
                          className="hover:text-brand-primary transition-colors hover:underline"
                        >
                          {r.productName}
                        </Link>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="bg-surface-subtle text-ink-muted border-border/40 inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-mono text-[11px] font-bold">
                          {r.sku}
                        </span>
                      </td>
                      <td className="text-ink py-3 pr-4 font-medium">
                        <Link
                          to={`/customers/${r.userId}`}
                          className="hover:text-brand-primary transition-colors hover:underline"
                        >
                          {r.customer}
                        </Link>
                      </td>
                      <td className="text-ink-muted py-3 pr-5 text-right text-[12px] font-medium">
                        {new Date(r.addedAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function UserSearchesReportPage() {
  const [range, setRange] = useState<DateRangeParams>({});
  const searches = useQuery({
    queryKey: ['report-searches', range],
    queryFn: () => api.reports.searches(range),
  });
  const rows = searches.data ?? [];
  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
          <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
            <Search className="h-4 w-4" strokeWidth={2} />
          </div>
          <span className="text-ink text-[14px] font-bold">Top Queries</span>
        </div>
        <DateRangeBar
          range={range}
          onApply={setRange}
          onExport={() => window.open(api.reports.exportSearchesUrl(range), '_blank')}
        />

        <div className="p-0">
          {searches.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <NothingFound />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Search Query</th>
                    <th className="py-3 pr-4 text-center">Times Searched</th>
                    <th className="py-3 pr-5 text-right">Last Searched On</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((r: SearchRow, idx: number) => (
                    <tr key={r.query} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="text-ink py-3 pr-4 font-bold">
                        <Link
                          to={`/products/all?search=${encodeURIComponent(r.query)}`}
                          className="hover:text-brand-primary transition-colors hover:underline"
                        >
                          &quot;{r.query}&quot;
                        </Link>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <span className="bg-ink inline-flex min-w-[28px] items-center justify-center rounded-md px-2 py-0.5 text-[11px] font-extrabold text-white shadow-sm">
                          {r.count}
                        </span>
                      </td>
                      <td className="text-ink-muted py-3 pr-5 text-right text-[12px] font-medium">
                        {new Date(r.lastSearchedAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// COMMISSION HISTORY
// ══════════════════════════════════════════════════════════════════

const COMMISSION_TONE: Record<string, BadgeTone> = {
  PENDING: 'warning',
  SETTLED: 'success',
  REVERSED: 'danger',
};

export function CommissionHistoryReportPage() {
  const [range, setRange] = useState<DateRangeParams>({});
  const commissions = useQuery({
    queryKey: ['report-commissions', range],
    queryFn: () => api.reports.commissions(range),
  });
  const rows = commissions.data ?? [];
  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-green-50 text-green-600 ring-1 ring-inset ring-green-200">
            <Percent className="h-4 w-4" strokeWidth={2} />
          </div>
          <span className="text-ink text-[14px] font-bold">Earnings Log</span>
        </div>
        <DateRangeBar
          range={range}
          onApply={setRange}
          onExport={() => window.open(api.reports.exportCommissionsUrl(range), '_blank')}
        />

        <div className="p-0">
          {commissions.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <NothingFound />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Order Number</th>
                    <th className="py-3 pr-4 text-center">Rate</th>
                    <th className="py-3 pr-4 text-right">Commission</th>
                    <th className="py-3 pr-4 text-center">Status</th>
                    <th className="py-3 pr-5 text-right">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((r: CommissionRow, idx: number) => (
                    <tr key={r.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="py-3 pr-4">
                        <Link
                          to={`/orders/${r.orderGroupId}`}
                          className="text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/20 border-brand-primary/20 inline-block rounded-md border px-2 py-0.5 font-mono text-[12px] font-bold transition-colors"
                        >
                          #{r.orderNumber}
                        </Link>
                      </td>
                      <td className="text-ink py-3 pr-4 text-center font-bold">
                        {(r.bps / 100).toFixed(2)}%
                      </td>
                      <td className="py-3 pr-4 text-right text-[14px] font-extrabold text-green-600">
                        +{formatPaise(r.amountPaise)}
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <Badge tone={COMMISSION_TONE[r.status] ?? 'neutral'}>{r.status}</Badge>
                      </td>
                      <td className="text-ink-muted py-3 pr-5 text-right text-[12px] font-medium">
                        {new Date(r.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// WALLET RECHARGE HISTORY
// ══════════════════════════════════════════════════════════════════

const WALLET_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'success',
  PARTIALLY_USED: 'warning',
  FULLY_USED: 'neutral',
  EXPIRED: 'neutral',
  REVOKED: 'danger',
};

export function WalletRechargeReportPage() {
  const [range, setRange] = useState<DateRangeParams>({});
  const recharges = useQuery({
    queryKey: ['report-wallet', range],
    queryFn: () => api.reports.walletRecharges(range),
  });
  const rows = recharges.data ?? [];
  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-200">
            <Wallet className="h-4 w-4" strokeWidth={2} />
          </div>
          <span className="text-ink text-[14px] font-bold">Wallet Transactions</span>
        </div>
        <DateRangeBar
          range={range}
          onApply={setRange}
          onExport={() => window.open(api.reports.exportWalletRechargesUrl(range), '_blank')}
        />

        <div className="p-0">
          {recharges.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <NothingFound />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Customer</th>
                    <th className="py-3 pr-4">Bucket</th>
                    <th className="py-3 pr-4 text-right">Amount</th>
                    <th className="py-3 pr-4 text-right">Remaining</th>
                    <th className="py-3 pr-4 text-center">Status</th>
                    <th className="py-3 pr-5 text-right">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((r: WalletRechargeRow, idx: number) => (
                    <tr key={r.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="text-ink py-3 pr-4 font-semibold">
                        <Link
                          to={`/customers/${r.userId}`}
                          className="hover:text-brand-primary transition-colors hover:underline"
                        >
                          {r.customer}
                        </Link>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge tone="neutral">{r.bucket}</Badge>
                      </td>
                      <td className="text-ink py-3 pr-4 text-right text-[14px] font-extrabold">
                        +{formatPaise(r.amountPaise)}
                      </td>
                      <td className="text-ink-muted py-3 pr-4 text-right font-bold">
                        {formatPaise(r.remainingPaise)}
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <Badge tone={WALLET_TONE[r.status] ?? 'neutral'}>
                          {r.status.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="text-ink-muted py-3 pr-5 text-right text-[12px] font-medium">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
